import { Injectable } from '@angular/core';
import { EventPipelineService } from '@bootstrap/services/event-pipeline.service';
import { EventBusService } from '@bootstrap/services/event-bus.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { PatientCardId } from '@shared/models/consts';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { EventName, PatientSaved, PatientSaveRequested, PatientClearRequested } from '@itero/itero-client-communication';

/**
 * Root-level service owning outbound EventBus commands to the Patient Card MFE.
 *
 * Responsibilities:
 * - savePatient(): publish patientSaveRequested → await patientSaved response
 * - clearPatient(): publish patientClearRequested (fire-and-forget)
 *
 * Both PatientCardFacade (component-scoped, inbound events) and PatientAppAdapter
 * (root-scoped, mode routing) delegate here. No logic duplication.
 */
@Injectable({ providedIn: 'root' })
export class PatientCardCommunicationService {
	private readonly moduleName = 'PatientCardCommunicationService';

	readonly patientSaved$ = this.eventPipeline.fromEvent<PatientSaved>(EventName.patientSaved);

	constructor(
		private readonly eventBus: EventBusService,
		private readonly eventPipeline: EventPipelineService,
		private readonly logger: LoggerService
	) {}

	savePatient(): Observable<boolean> {
		return from(this.eventBus.publish<PatientSaveRequested>(EventName.patientSaveRequested, [PatientCardId])).pipe(
			switchMap(() =>
				this.patientSaved$.pipe(
					take(1),
					map(event => event.data.success)
				)
			),
			catchError(err => {
				this.logger.error('patient-save-requested publish failed', { module: this.moduleName, error: err as Error });

				return of(false);
			})
		);
	}

	clearPatient(): void {
		this.eventBus
			.publish<PatientClearRequested>(EventName.patientClearRequested, [PatientCardId])
			.catch(err => this.logger.error('patient-clear-requested publish failed', { module: this.moduleName, error: err as Error }));
	}
}
